import React, { type ReactElement } from 'react';
import type {
  TrolleyQuantity,
  UOM,
} from '@johnlewispartnership/wtr-ingredients/ingredients/ProductPod/types';
import type { ChipProps } from '@johnlewispartnership/wtr-ingredients/ingredients/Chips';
import { BadgeType } from '@johnlewispartnership/wtr-ingredients/ingredients/Badge/types';
import { C62, weightOptions, type WeightUnits } from 'constants/weightOptions';
import { getClickContext } from 'utils/clickContext';
import {
  Argentina,
  Armenia,
  Australia,
  Austria,
  AwardWinning,
  British,
  Canada,
  California,
  Chile,
  England,
  France,
  Georgia,
  Germany,
  Greece,
  Hungary,
  India,
  Israel,
  Italy,
  Information,
  Jersey,
  Lebanon,
  Macedonia,
  Moldova,
  NewZealand,
  Portugal,
  Romania,
  Slovenia,
  SouthAfrica,
  Spain,
  Turkey,
  UnitedStates,
  Wales,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { GTM_TRACKED_CONFLICTS, AVAILABLE, UNAVAILABLE } from 'constants/conflicts';
import type { LineNumber, MarketingBadge, ProductTag } from 'constants/products';

export function toDSPodUom(uomId: string) {
  const uom = weightOptions[uomId as WeightUnits];

  return uom ? { ...uom, id: uom.unit } : undefined;
}

export type DSPodUoms =
  | {
      item: UOM;
      weight?: UOM;
    }
  | {
      item?: UOM;
      weight: UOM;
    };

export function toDSPodUoms(uoms: string[]): DSPodUoms {
  const weightUoms = uoms.filter(unit => unit !== C62);
  const weight = weightUoms.length > 0 ? toDSPodUom(weightUoms[0]) : undefined;
  const item = uoms.includes(C62) ? toDSPodUom(C62) : undefined;

  return item || weight
    ? ({
        ...(item ? { item } : {}),
        ...(weight ? { weight } : {}),
      } as DSPodUoms)
    : ({ item: toDSPodUom(C62) } as DSPodUoms);
}

export type UpdateTrolleyContext = {
  lineNumber: LineNumber;
  productId: string;
  position: number;
  clickContext?: ReturnType<typeof getClickContext>;
};

export const updateTrolleyParams = (
  quantity: TrolleyQuantity,
  event: Parameters<typeof getClickContext>[0],
  ctx: UpdateTrolleyContext,
  quantityKey = 'quantity',
) => ({
  [quantityKey]: { amount: quantity.amount, uom: quantity.uomId },
  lineNumber: ctx.lineNumber,
  productId: ctx.productId,
  productPosition: ctx.position,
  clickContext: ctx.clickContext || getClickContext(event),
});

export const POD_TYPE_LIGHT = 'light' as const;
export const POD_TYPE_REGULAR = 'regular' as const;
export const POD_TYPE_THUMBNAIL = 'thumbnail' as const;
export const POD_TYPE_SPONSORED = 'sponsored' as const;
export const POD_TYPE_CLICK_TO_BUY = 'click-to-buy' as const;

export type PodDataset = {
  'data-product-availability': string;
  'data-product-id': string;
  'data-product-name': string;
  'data-product-type': string;
  'data-product-on-offer': boolean;
  'data-product-pod-type': string;
  'data-product-index': number;
  'data-product-sponsored': string | null;
};

export function makePodDataset({
  lineNumber,
  hasPromotions,
  name,
  productType,
  productPosition,
  conflictMessage,
  podType,
  sponsored,
  searchType,
  searchTags,
  searchCategory,
  vendor = 'citrusAd',
}: {
  lineNumber: LineNumber;
  hasPromotions: boolean;
  name: string;
  productType: string;
  productPosition: number;
  conflictMessage?: string;
  podType: string;
  sponsored?: boolean;
  searchType?: string;
  searchTags?: string;
  searchCategory?: string;
  vendor?: string;
}): PodDataset {
  let availability = AVAILABLE;

  if (conflictMessage) {
    availability = GTM_TRACKED_CONFLICTS.includes(conflictMessage) ? conflictMessage : UNAVAILABLE;
  }

  return {
    'data-product-availability': availability,
    'data-product-id': lineNumber,
    'data-product-name': name,
    'data-product-type': productType,
    'data-product-on-offer': hasPromotions,
    'data-product-pod-type': podType,
    'data-product-index': productPosition,
    'data-product-sponsored': sponsored
      ? `${vendor}-SponsoredProduct-${searchType}-${[searchTags, searchCategory]
          .filter(Boolean)
          .join('/')}-${lineNumber}-${productPosition}`
      : null,
  };
}

const badgeObjectConverter: Record<string, { type: BadgeType }> = {
  'Award winner': { type: BadgeType.GREEN },
  'Best in season': { type: BadgeType.GREEN },
  Christmas: { type: BadgeType.SEASONAL },
  'Coronation edition': { type: BadgeType.LIMITED_EDITION },
  'Everyday value': { type: BadgeType.VALUE_GREEN },
  'Meal deal': { type: BadgeType.OFFER },
  New: { type: BadgeType.GREEN },
  'New lower price': { type: BadgeType.VALUE_GREEN },
  'Online offer': { type: BadgeType.OFFER },
  'Online only offer': { type: BadgeType.OFFER },
  'Special buy': { type: BadgeType.OFFER },
  'Wine event': { type: BadgeType.WINE_EVENT },
};

export const ALLERGEN_UPDATE_PRODUCT_TAG_NAME = 'Allergen update' as const;

export enum ProductTagType {
  GREY = 'grey',
  BLUE = 'blue',
  RED = 'red',
  RED_ALTERNATIVE = 'redAlternative',
  GREEN = 'green',
  GREEN_ALTERNATIVE = 'greenAlternative',
  YELLOW = 'yellow',
  AWARD = 'award',
}

// any tag not appearing on the list will be displayed with the default chip color (grey)
const tagObjectConverter: Record<string, { type: ChipProps['colour']; icon?: ReactElement }> = {
  [ALLERGEN_UPDATE_PRODUCT_TAG_NAME]: { type: ProductTagType.RED },
  'Award winning': { type: ProductTagType.AWARD, icon: <AwardWinning /> },
  BBQ: { type: ProductTagType.GREEN_ALTERNATIVE },
  'd+': { type: ProductTagType.GREEN_ALTERNATIVE },
  'Fresh until': { type: ProductTagType.GREEN_ALTERNATIVE },
  'Fresher for longer': { type: ProductTagType.GREEN_ALTERNATIVE },
  Frozen: { type: ProductTagType.BLUE },
  Freezable: { type: ProductTagType.BLUE },
  'Fruit & Vanilla': { type: ProductTagType.YELLOW },
  'Fruits & Honey': { type: ProductTagType.GREEN_ALTERNATIVE },
  'Long life': { type: ProductTagType.GREEN_ALTERNATIVE },
  'In season': { type: ProductTagType.GREEN_ALTERNATIVE },
  'New Look': { type: ProductTagType.GREEN },
  'New Recipe': { type: ProductTagType.RED },
  'Slower reared': { type: ProductTagType.GREEN, icon: <British /> },
  'Spice & Caramel': { type: ProductTagType.RED },
  // COUNTRIES LIST
  Argentina: { type: ProductTagType.BLUE, icon: <Argentina /> },
  Armenia: { type: ProductTagType.YELLOW, icon: <Armenia /> },
  Australia: { type: ProductTagType.BLUE, icon: <Australia /> },
  Austria: { type: ProductTagType.RED, icon: <Austria /> },
  British: { type: ProductTagType.BLUE, icon: <British /> },
  California: { type: ProductTagType.RED, icon: <California /> },
  Canada: { type: ProductTagType.RED, icon: <Canada /> },
  Chile: { type: ProductTagType.BLUE, icon: <Chile /> },
  England: { type: ProductTagType.RED, icon: <England /> },
  France: { type: ProductTagType.BLUE, icon: <France /> },
  'Free range': { type: ProductTagType.GREEN_ALTERNATIVE },
  Georgia: { type: ProductTagType.RED, icon: <Georgia /> },
  Germany: { type: ProductTagType.YELLOW, icon: <Germany /> },
  Greece: { type: ProductTagType.BLUE, icon: <Greece /> },
  Hungary: { type: ProductTagType.GREEN, icon: <Hungary /> },
  India: { type: ProductTagType.GREEN, icon: <India /> },
  Israel: { type: ProductTagType.BLUE, icon: <Israel /> },
  Italy: { type: ProductTagType.GREEN, icon: <Italy /> },
  Jersey: { type: ProductTagType.RED, icon: <Jersey /> },
  Lebanon: { type: ProductTagType.GREEN, icon: <Lebanon /> },
  Macedonia: { type: ProductTagType.YELLOW, icon: <Macedonia /> },
  Moldova: { type: ProductTagType.YELLOW, icon: <Moldova /> },
  'New Zealand': { type: ProductTagType.BLUE, icon: <NewZealand /> },
  'North Macedonia': { type: ProductTagType.YELLOW, icon: <Macedonia /> },
  Organic: { type: ProductTagType.GREEN_ALTERNATIVE },
  Portugal: { type: ProductTagType.GREEN, icon: <Portugal /> },
  Romania: { type: ProductTagType.YELLOW, icon: <Romania /> },
  Slovenia: { type: ProductTagType.BLUE, icon: <Slovenia /> },
  'South Africa': { type: ProductTagType.GREEN, icon: <SouthAfrica /> },
  Spain: { type: ProductTagType.YELLOW, icon: <Spain /> },
  Turkey: { type: ProductTagType.RED, icon: <Turkey /> },
  USA: { type: ProductTagType.BLUE, icon: <UnitedStates /> },
  Wales: { type: ProductTagType.GREEN, icon: <Wales /> },
};

export const mapDataToBadges = (
  productData: { marketingBadges?: ReadonlyArray<MarketingBadge> } | null | undefined,
): Array<MarketingBadge & { type?: BadgeType }> | null => {
  if (!productData?.marketingBadges?.length) {
    return null;
  }

  return productData.marketingBadges.map(badge => {
    const badgeName = badge.name;

    return badgeName && badgeObjectConverter[badgeName]
      ? { ...badge, ...badgeObjectConverter[badgeName] }
      : badge;
  });
};

export const mapDataToTags = (
  productData: { productTags?: ReadonlyArray<ProductTag> } | null | undefined,
): Array<
  ProductTag & { icon?: ReactElement; trailingIcon?: ReactElement; type?: ChipProps['colour'] }
> | null => {
  if (!productData?.productTags?.length) {
    return null;
  }

  return productData.productTags.map(tag => {
    if (tag.name.includes('d+')) {
      return { name: tag.name, ...tagObjectConverter['d+'] };
    }

    if (tag.name.includes('Fresh until')) {
      return { name: tag.name, ...tagObjectConverter['Fresh until'] };
    }

    return tagObjectConverter[tag.name]
      ? {
          ...tag,
          ...tagObjectConverter[tag.name],
          ...(tag.tooltip ? { trailingIcon: <Information /> } : {}),
        }
      : tag;
  });
};
